/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useCallback, useContext, useMemo,useState } from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { message } from 'antd';
import { collection } from 'firebase/firestore';

import { FSCollections } from '@src/constants/database';
import { db } from '@src/firebase/firebase';
import HolidayService from '@src/services/HolidayService';

import { useCollectionWithSubcollectionsPaginated } from 'packages/utils';

const HolidaysContext = createContext();
const holidayService = new HolidayService();

export const HolidaysProvider = ({ children }) => {
  const { ORGANIZATIONS, HOLIDAYS } = FSCollections;
  const intl = useIntl();

  const orgId = useSelector(({ organizations }) => organizations.organization.id);
  const [refreshTrigger, setRefreshTrigger] = useState(1);
  const [loading, setLoading] = useState(false);

  const holidaysQuery = orgId ? collection(db, ORGANIZATIONS, orgId, HOLIDAYS) : null;
  const { data: holidays = [], loading: holidaysLoading } =
    useCollectionWithSubcollectionsPaginated(holidaysQuery, [], 50, [orgId, refreshTrigger]);

  const sortedHolidays = useMemo(() => {
    return holidays.sort((a, b) => new Date(a.holiday.date) - new Date(b.holiday.date));
  }, [holidays]);

  const createHoliday = useCallback(
    async holidayData => {
      try {
        setLoading(true);
        const result = await holidayService.createHoliday(holidayData, orgId);
        setRefreshTrigger(prev => prev + 1);
        return result;
      } catch (error) {
        message.error(`Error al crear el feriado: ${error.message}`);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [orgId],
  );

  const updateHoliday = useCallback(
    async (holidayId, holidayData) => {
      try {
        setLoading(true);
        const result = await holidayService.updateHoliday(holidayId, holidayData, orgId);
        setRefreshTrigger(prev => prev + 1);
        return result;
      } catch (error) {
        message.error(`Error al actualizar el feriado: ${error.message}`);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [orgId],
  );

  const deleteHoliday = useCallback(
    async holidayId => {
      try {
        setLoading(true);
        await holidayService.deleteHoliday(holidayId, orgId);
        setRefreshTrigger(prev => prev + 1);
      } catch (error) {
        message.error(`Error al eliminar el feriado: ${error.message}`);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [orgId],
  );

  const syncHolidays = useCallback(
    async orgId => {
      try {
        setLoading(true);
        const result = await holidayService.syncHolidays(orgId);
        message.success(intl.formatMessage({ id: 'holiday.messages.syncSuccess' }));
        setRefreshTrigger(prev => prev + 1);
        return result;
      } catch (error) {
        message.error(
          intl.formatMessage({ id: 'holiday.messages.syncError' }) + `: ${error.message}`,
        );
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [orgId],
  );

  const value = {
    holidays: sortedHolidays,
    refreshTrigger,
    setRefreshTrigger,
    loading: holidaysLoading || loading,
    createHoliday,
    updateHoliday,
    deleteHoliday,
    syncHolidays,
  };

  return <HolidaysContext.Provider value={value}>{children}</HolidaysContext.Provider>;
};

export const useHolidays = () => {
  const context = useContext(HolidaysContext);
  if (!context) {
    throw new Error('useHolidays debe ser usado dentro de un HolidaysProvider');
  }
  return context;
};
